import {
  liquidGlassCircleShadowSx,
  liquidGlassPressTransition,
  liquidGlassSurfaceFillSx,
  LIQUID_GLASS_CIRCLE_PRESS_SCALE,
  LIQUID_GLASS_PRESS_ORIGIN,
  useLiquidGlassPress,
} from '@focuslee/shared-mini-app';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import type { FC } from 'react';
import { CommonIcon } from './CommonIcon';

interface LiquidGlassCircleButtonProps {
  icon: string;
  ariaLabel: string;
  onClick: () => void;
  size?: number;
  iconSize?: number;
  disabled?: boolean;
  color?: string;
}

export const LiquidGlassCircleButton: FC<LiquidGlassCircleButtonProps> = ({
  icon,
  ariaLabel,
  onClick,
  size = 56,
  iconSize = 24,
  disabled = false,
  color = 'var(--tg-theme-text-color)',
}) => {
  const {
    pressed,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onClick: handleClick,
  } = useLiquidGlassPress({ disabled, onActivate: onClick });

  return (
    <Box
      component={motion.div}
      initial={false}
      animate={{ scale: pressed ? LIQUID_GLASS_CIRCLE_PRESS_SCALE : 1 }}
      transition={liquidGlassPressTransition}
      sx={{
        flexShrink: 0,
        width: size,
        transformOrigin: LIQUID_GLASS_PRESS_ORIGIN,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          overflow: 'visible',
          ...liquidGlassCircleShadowSx,
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label={ariaLabel}
          onClick={handleClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
            border: 'none',
            cursor: disabled ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            pointerEvents: 'auto',
            WebkitTapHighlightColor: 'transparent',
            outline: 'none',
            ...liquidGlassSurfaceFillSx,
            '&:focus-visible': {
              outline: '2px solid var(--tg-theme-link-color)',
              outlineOffset: 2,
            },
          }}
        >
          <CommonIcon icon={icon} type="filled" size={iconSize} color="currentColor" />
        </Box>
      </Box>
    </Box>
  );
};
